import { LOCAL_DOMAIN_NAME_FOR_TESTING } from '../App';

// Routes that opt out of PWA features (no install prompt, no SW registration).
// Pathnames are matched case-insensitively after trailing-slash normalization.
const PWA_EXCLUDED_ROUTES = ['/hubspot/configure', '/hubspot/cobrowse'];

// Hosts that opt out of PWA features. These are the reserved hosts where the
// SubDomainLogin page is rendered (the user hasn't picked a tenant subdomain
// yet, so an install would brand to the wrong app). Keep in sync with
// RedirectSubdomainForReservedHosts in src/shared/utility.js and the inline
// list in public/index.html.
const PWA_EXCLUDED_HOSTS = ['journeydev.blitzz.co', 'rc.blitzz.co', 'web.blitzz.co'];

// Hostnames that, in combination with LOCAL_DOMAIN_NAME_FOR_TESTING, simulate
// running on a reserved host during local development. Mirrors the localhost
// alias in getIsRedirectSubdomainForReservedHost (src/shared/utility.js).
const LOCAL_TEST_HOSTNAMES = ['localhost', 'agentdev.blitzz.co'];

const normalizePathname = (pathname) => {
  if (typeof pathname !== 'string') return '';
  const trimmed = pathname.replace(/\/+$/, '') || '/';
  return trimmed.toLowerCase();
};

export const isPwaExcludedRoute = (pathname) => {
  const target = normalizePathname(pathname);
  return PWA_EXCLUDED_ROUTES.some((route) => normalizePathname(route) === target);
};

export const isPwaExcludedHost = (host) => {
  if (typeof host !== 'string') return false;
  const target = host.toLowerCase();
  if (PWA_EXCLUDED_HOSTS.some((h) => h.toLowerCase() === target)) return true;
  // Local dev: when the actual host is localhost/agentdev and
  // LOCAL_DOMAIN_NAME_FOR_TESTING points at an excluded host, suppress PWA
  // here too so the SubDomainLogin behaviour can be exercised locally.
  const hostname = typeof window !== 'undefined' ? (window.location.hostname || '').toLowerCase() : '';
  if (LOCAL_TEST_HOSTNAMES.includes(hostname) && typeof LOCAL_DOMAIN_NAME_FOR_TESTING === 'string') {
    const simulated = LOCAL_DOMAIN_NAME_FOR_TESTING.toLowerCase();
    return PWA_EXCLUDED_HOSTS.some((h) => h.toLowerCase() === simulated);
  }
  return false;
};

export const isPwaExcluded = (pathname, host) =>
  isPwaExcludedRoute(pathname) || isPwaExcludedHost(host);

const MANIFEST_LINK_ID = 'pwa-manifest';
const MANIFEST_HREF = '/manifest.json';

// Toggle the <link rel="manifest"> node so Chromium hides the address-bar
// install icon on excluded routes. The same list is enforced in index.html
// for the initial paint (before React mounts).
export const setManifestLinkEnabled = (enabled) => {
  if (typeof document === 'undefined') return;
  let link = document.getElementById(MANIFEST_LINK_ID);
  if (enabled) {
    if (link) return;
    link = document.createElement('link');
    link.id = MANIFEST_LINK_ID;
    link.rel = 'manifest';
    link.href = MANIFEST_HREF;
    document.head.appendChild(link);
  } else if (link && link.parentNode) {
    link.parentNode.removeChild(link);
  }
};

// Clean up any service worker + caches left behind from a previous visit.
// Only safe to call when the entire HOST is PWA-excluded — never call this
// on a route-only exclusion (e.g. /hubspot/configure on a tenant subdomain),
// since the host still has a legitimate PWA we'd otherwise destroy.
export const unregisterServiceWorker = () => {
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations()
      .then((registrations) => {
        registrations.forEach((registration) => registration.unregister());
      })
      .catch(() => {});
  }
  if (typeof caches !== 'undefined') {
    caches.keys()
      .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      .catch(() => {});
  }
};

export const registerServiceWorker = () => {
  if (!('serviceWorker' in navigator)) return;
  // Comment below line to test PWA on local `npm start`
  // if (process.env.NODE_ENV !== 'production') return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js', { scope: '/' })
      .then((registration) => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch(() => {});
  });
};

export const isStandalone = () => {
  const matchesStandalone =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  return (
    matchesStandalone ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://')
  );
};

export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

export const isIOSSafari = () => {
  const ua = navigator.userAgent;
  return isIOS() && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
};

export const isMacSafari = () => {
  const ua = navigator.userAgent;
  return /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
};

export const isAndroid = () => /Android/.test(navigator.userAgent);
